import {
    ActionTypes,
    ActorRefFrom,
    BaseActionObject,
    DoneInvokeEvent,
    InterpreterFrom,
    ResolveTypegenMeta,
    ServiceMap,
    StateMachine,
    TypegenDisabled,
} from "xstate"
import { Materialized } from "@silversea-ssc/functional-support"

export type InitialDataLoadContext = {
    id: string
}

type LoadedContextFields<ASuccess> = {
    successes: ASuccess[]
    failures: (Error | Response)[]
}

export type LoadedContext<ASuccess> = InitialDataLoadContext & LoadedContextFields<ASuccess>

type FatalError = {
    type: "FATAL"
    message: string
}

type ErrorContextFields = { error: FatalError }

export type ErrorContext = InitialDataLoadContext & ErrorContextFields

export type DataLoadTypeState<ASuccess> =
    | { value: "idle"; context: InitialDataLoadContext }
    | { value: "loading"; context: InitialDataLoadContext }
    | { value: "loaded"; context: LoadedContext<ASuccess> }
    | { value: "error"; context: ErrorContext }

export type DataLoadContext<ASuccess> = InitialDataLoadContext &
    Partial<LoadedContextFields<ASuccess>> &
    Partial<ErrorContextFields>

export type LoadCompleted<ASuccess> = { type: "LOAD_COMPLETED"; successes: ASuccess[]; failures: (Response | Error)[] }
export type LoadFatalError = { type: "LOAD_FATAL_ERROR"; message: string }
export type Load = { type: "LOAD"; request: Request[] }
export type DataLoadEvents<ASuccess> =
    | Load
    | LoadCompleted<ASuccess>
    | LoadFatalError
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | (DoneInvokeEvent<any> & { type: ActionTypes.DoneInvoke })

export type DataLoadStateMachine<ASuccess> = StateMachine<
    DataLoadContext<ASuccess>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    DataLoadEvents<ASuccess>,
    DataLoadTypeState<ASuccess>,
    BaseActionObject,
    ServiceMap,
    ResolveTypegenMeta<TypegenDisabled, DataLoadEvents<ASuccess>, BaseActionObject, ServiceMap>
>

type DataLoadInterpreter<ASuccess> = InterpreterFrom<DataLoadStateMachine<ASuccess>>
type DataLoadState<ASuccess> = DataLoadInterpreter<ASuccess>["state"]
type DataLoadSend<ASuccess> = DataLoadInterpreter<ASuccess>["send"]
type ActorRef<ASuccess> = ActorRefFrom<DataLoadStateMachine<ASuccess>>
type DataLoadTypes<ASuccess> = {
    Context: DataLoadContext<ASuccess>
    Events: DataLoadEvents<ASuccess>
    LoadCompleted: LoadCompleted<ASuccess>
    LoadFatalError: LoadFatalError
    Load: Load
    TypeState: DataLoadTypeState<ASuccess>
    StateMachine: DataLoadStateMachine<ASuccess>
    Interpreter: DataLoadInterpreter<ASuccess>
    State: DataLoadState<ASuccess>
    Send: DataLoadSend<ASuccess>
    ActorRef: ActorRef<ASuccess>
}
export const httpDataLoadTypes = <ESuccess, ASuccess>(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Success: Materialized<ESuccess, ASuccess>,
): DataLoadTypes<ASuccess> => ({} as DataLoadTypes<ASuccess>)
